import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { FaEdit, FaTrash, FaPlus, FaSave } from "react-icons/fa";

// Import Google Fonts
import "@fontsource/merriweather/700.css"; // Headings
import "@fontsource/bree-serif"; // Main content (form)
import "@fontsource/prompt/400.css"; // Table content
import "@fontsource/prompt/500.css";

const PromptFontSample = () => {
  const { skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);

  const [groupName, setGroupName] = useState("");
  const [groupTypeId, setGroupTypeId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [groupList, setGroupList] = useState([]);
  const [groupTypes, setGroupTypes] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editId, setEditId] = useState(null);

  
  useEffect(() => {
    setGroupTypes([
      { fld_grouptypeid: 1, fld_grouptypename: "Assets" },
      { fld_grouptypeid: 2, fld_grouptypename: "Liabilities" },
      { fld_grouptypeid: 3, fld_grouptypename: "Income" },
      { fld_grouptypeid: 4, fld_grouptypename: "Expenses" },
    ]);
    setGroupList([
      { groupid: 1, groupname: "Cash", grouptype: "Assets" },
      { groupid: 2, groupname: "Bank Loans", grouptype: "Liabilities" },
    ]);
  }, []);

  const handleAddNew = () => {
    setEditId(null);
    setGroupName("");
    setGroupTypeId("");
    setRemarks("");
  };

  const handleSave = () => {
    if (!groupName || !groupTypeId) {
      alert("Please fill required fields");
      return;
    }
    const newGroup = {
      groupid: Date.now(),
      groupname: groupName,
      grouptype:
        groupTypes.find(
          (g) => g.fld_grouptypeid === parseInt(groupTypeId)
        )?.fld_grouptypename || "",
    };
    setGroupList([...groupList, newGroup]);
    handleAddNew();
  };

  const handleSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = () => {
    setGroupList((prev) => prev.filter((g) => !selectedRows.includes(g.groupid)));
    setSelectedRows([]);
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title="ACCOUNT GROUP MASTER"
        setSkin={setSkin}
        currentSkin={skin}
        style={{ fontFamily: "'Merriweather', serif" }}
      />
      <div className="bg-white p-6 rounded shadow m-4">
        {/* Form */}
        <h2
          className="text-2xl mb-4"
          style={{ fontFamily: "'Merriweather', serif" }}
        >
          Add / Edit Group
        </h2>
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
          style={{ fontFamily: "'Bree Serif', serif" }}
        >
          <div>
            <label className="block font-medium mb-1">Group Name *</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Group Type *</label>
            <select
              className="w-full p-2 border rounded"
              value={groupTypeId}
              onChange={(e) => setGroupTypeId(e.target.value)}
            >
              <option value="">-- Select --</option>
              {groupTypes.map((type) => (
                <option key={type.fld_grouptypeid} value={type.fld_grouptypeid}>
                  {type.fld_grouptypename}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-1">Remarks</label>
            <textarea
              className="w-full p-2 border rounded"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={1}
            ></textarea>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-4 mb-6" style={{ fontFamily: "'Bree Serif', serif" }}>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2"
            onClick={handleAddNew}
          >
            <FaPlus /> Add New
          </button>
          <button
            className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
            onClick={handleSave}
          >
            <FaSave /> Save
          </button>
          <button
            className="bg-red-500 text-white px-4 py-2 rounded flex items-center gap-2"
            onClick={handleDeleteSelected}
          >
            <FaTrash /> Remove
          </button>
        </div>

        {/* Table */}
        <h2
          className="text-2xl mb-4"
          style={{ fontFamily: "'Merriweather', serif" }}
        >
          Group List
        </h2>
        <div className="overflow-x-auto">
          <table
            className="min-w-full border text-sm"
            style={{ fontFamily: "'Prompt', sans-serif" }}
          >
            <thead className="bg-blue-200 text-gray-800">
              <tr>
                <th className="border px-3 py-2">S.No</th>
                <th className="border px-3 py-2">Group Name</th>
                <th className="border px-3 py-2">Group Type</th>
                <th className="border px-3 py-2">Select</th>
                <th className="border px-3 py-2">Edit</th>
              </tr>
            </thead>
            <tbody>
              {groupList.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center text-gray-500 py-4"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                groupList.map((item, idx) => (
                  <tr
                    key={item.groupid}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-100"}
                  >
                    <td className="border px-3 py-2 text-center">{idx + 1}</td>
                    <td className="border px-3 py-2">{item.groupname}</td>
                    <td className="border px-3 py-2">{item.grouptype}</td>
                    <td className="border px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.groupid)}
                        onChange={() => handleSelect(item.groupid)}
                      />
                    </td>
                    <td className="border px-3 py-2 text-center">
                      <button
                        onClick={() => {
                          setEditId(item.groupid);
                          setGroupName(item.groupname);
                          setGroupTypeId(
                            groupTypes.find(
                              (g) => g.fld_grouptypename === item.grouptype
                            )?.fld_grouptypeid || ""
                          );
                          setRemarks("");
                        }}
                      >
                        <FaEdit className="text-blue-600" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default PromptFontSample;
